/**
 * Give cash (owner's request, 21 Oct 2026): a spender hands part of the cash he holds to another
 * spender of the same portfolio, in one step. It leaves his wallet at once; the other person sees
 * the usual "… gave you Rs …" with Received, exactly as when the owner gives cash.
 */
import { useEffect, useState, type FormEvent } from 'react';

import { api } from '../api/index.ts';
import { ApiError, type Handover, type HandoverPerson } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { rs } from '../lib/money.ts';
import { goWalletHome, useRoute } from '../lib/router.ts';
import { EntityIcon, Icon } from '../ui/icons.tsx';
import { Num, Spinner } from '../ui/parts.tsx';
import { mayGiveCash } from '../admin/perms.ts';
import { useSpender } from './store.tsx';

const digits = (s: string) => s.replace(/\D/g, '').slice(0, 7);
const choice =
  'flex min-h-14 items-center gap-2.5 rounded-xl border-[1.5px] px-3 py-2 text-start text-[15px] font-semibold focus-visible:outline-3 focus-visible:outline-ink';

export function GiveCash() {
  const { t } = useT();
  const s = useSpender();
  // Only places where they have a wallet (cash of their own to give) and are the Owner or a
  // Super Admin: an office boy doesn't give cash on.
  const places = s.entities.filter(
    (e) => mayGiveCash(s.me, e.id) && s.wallets.some((w) => w.entityId === e.id && (!w.userId || w.userId === s.me.id)),
  );
  // Opened from a wallet card: that wallet, fixed (it is where the money comes from).
  const fixed = useRoute().query.get('entity');
  const [entityId, setEntityId] = useState(() =>
    fixed && places.some((e) => e.id === fixed)
      ? fixed
      : places.some((e) => e.id === s.lastEntityId)
        ? s.lastEntityId
        : (places[0]?.id ?? ''),
  );
  const wallet = s.wallets.find((w) => w.entityId === entityId && (!w.userId || w.userId === s.me.id));
  const inHand = wallet ? wallet.balance - wallet.unacknowledgedCashIn : 0;

  const [people, setPeople] = useState<HandoverPerson[] | null>(null);
  const [recent, setRecent] = useState<Handover[]>([]);
  const [toWalletId, setToWalletId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ text: string; undoId?: string } | null>(null);
  // Undo is offered for a few seconds after giving (owner's request, 21 Oct 2026).
  useEffect(() => {
    if (!done?.undoId) return;
    const timer = window.setTimeout(() => setDone((d) => (d ? { text: d.text } : d)), 7000);
    return () => window.clearTimeout(timer);
  }, [done?.undoId]);

  useEffect(() => {
    if (!entityId) return;
    let live = true;
    setPeople(null);
    setToWalletId('');
    Promise.all([api.handoverPeople(entityId), api.myHandovers(entityId)])
      .then(([p, r]) => {
        if (!live) return;
        setPeople(p);
        setRecent(r);
        if (p.length === 1) setToWalletId(p[0]?.walletId ?? '');
      })
      .catch(() => live && setPeople([]));
    return () => {
      live = false;
    };
  }, [entityId]);

  const person = people?.find((p) => p.walletId === toWalletId);
  const amt = Number(amount || 0);
  const tooMuch = amt > inHand;
  const valid = Boolean(person) && amt > 0 && !tooMuch;
  const entity = s.entityById(entityId);

  async function undoLast(id: string) {
    setError(null);
    try {
      await api.undo(id, entityId);
      setRecent((r) => r.filter((h) => h.id !== id));
      setDone({ text: t('give.undone') });
      await s.refresh();
    } catch (err) {
      setDone(null);
      setError(err instanceof ApiError ? err.message : t('app.error'));
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || busy || !person) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.handOver({ entityId, toWalletId: person.walletId, amount: amt, note });
      setDone({ text: t('give.done', { amount: rs(amt), name: person.name }), undoId: res.handover.id });
      setRecent((r) => [res.handover, ...r]);
      setAmount('');
      setNote('');
      await s.refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('app.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-app flex-col">
      <header className="safe-top shrink-0 border-b-[1.5px] border-line bg-paper">
        <div className="flex h-[60px] w-full items-center gap-1.5 ps-1 pe-4 sm:px-2 lg:px-6">
          <button type="button" aria-label={t('give.back')} onClick={goWalletHome} className="flex size-12 items-center justify-center">
            <Icon name="arrowStart" size={24} stroke={2.25} flip />
          </button>
          <h1 className="m-0 flex-1 text-[21px] font-bold rtl:text-[23px]">{t('give.title')}</h1>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col gap-4 px-4 pt-4 pb-8">
        <p className="m-0 text-[15px] text-muted">{t('give.intro')}</p>

        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
          {places.length > 1 && !fixed && (
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="mb-2 text-sm font-bold">{t('give.from')}</legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {places.map((p) => {
                  const w = s.wallets.find((x) => x.entityId === p.id && (!x.userId || x.userId === s.me.id));
                  const on = p.id === entityId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setEntityId(p.id)}
                      className={`${choice} ${on ? 'border-ink bg-ink text-white' : 'border-line-strong bg-paper hover:bg-ground'}`}
                    >
                      <span style={on ? undefined : { color: p.colour }}>
                        <EntityIcon entity={p} size={20} />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col leading-tight">
                        <span className="truncate">{p.name}</span>
                        <Num className="text-[13px] font-normal opacity-80">{rs(w ? w.balance - w.unacknowledgedCashIn : 0)}</Num>
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          <div className="rounded-xl bg-ground px-3.5 py-2.5 text-[15px] font-semibold">
            {entity?.name} · <Num>{t('give.inHand', { amount: rs(inHand) })}</Num>
          </div>

          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-2 text-sm font-bold">{t('give.to')}</legend>
            {people === null ? (
              <Spinner size={22} />
            ) : people.length === 0 ? (
              <p className="m-0 text-[15px] text-muted">{t('give.nobody', { entity: entity?.name ?? '' })}</p>
            ) : (
              <PersonPicker people={people} value={toWalletId} onChange={setToWalletId} />
            )}
          </fieldset>

          <label className="flex flex-col gap-2 text-sm font-bold">
            {t('give.amount')}
            <span
              className={`flex h-14 items-center gap-2 rounded-xl border-[1.5px] bg-paper px-3.5 focus-within:border-ink ${tooMuch ? 'border-danger' : 'border-line-strong'}`}
            >
              <span className="font-semibold text-muted">Rs</span>
              <input
                inputMode="numeric"
                value={amount ? Number(amount).toLocaleString('en-US') : ''}
                onChange={(e) => setAmount(digits(e.target.value))}
                className="num min-w-0 flex-1 border-0 bg-transparent text-2xl font-bold outline-none"
                placeholder="0"
              />
            </span>
            {tooMuch && <span className="font-semibold text-danger-text">{t('give.tooMuch', { amount: rs(inHand) })}</span>}
          </label>

          <label className="flex flex-col gap-2 text-sm font-bold">
            {t('give.note')}
            <input
              className="h-12 rounded-xl border-[1.5px] border-line-strong bg-paper px-3.5 text-[15px] font-normal"
              maxLength={200}
              value={note}
              placeholder={t('give.notePlaceholder')}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          {error && (
            <p role="alert" className="m-0 rounded-xl bg-danger-tint px-3.5 py-2.5 text-[15px] font-semibold text-danger-text">
              {error}
            </p>
          )}
          {done && (
            <p role="status" className="m-0 flex items-center gap-2 rounded-xl bg-success-tint px-3.5 py-2 text-[15px] font-bold text-success-text">
              <Icon name="check" size={18} stroke={2.5} />
              <span className="min-w-0 flex-1">{done.text}</span>
              {done.undoId && (
                <button
                  type="button"
                  onClick={() => void undoLast(done.undoId ?? '')}
                  className="h-10 shrink-0 rounded-lg bg-paper px-3.5 text-[15px] font-bold text-ink hover:bg-ground"
                >
                  {t('give.undo')}
                </button>
              )}
            </p>
          )}

          <button
            type="submit"
            disabled={!valid || busy}
            className="num flex h-14 items-center justify-center rounded-[14px] bg-brand px-4 text-[17px] font-bold text-white hover:bg-brand-dark disabled:bg-line disabled:text-muted"
          >
            {busy ? <Spinner size={22} /> : person && amt > 0 ? t('give.submit', { amount: rs(amt), name: person.name }) : t('give.submitEmpty')}
          </button>
        </form>

        <section className="mt-2 flex flex-col gap-2">
          <h2 className="m-0 text-sm font-bold text-muted">{t('give.recent')}</h2>
          {recent.length === 0 ? (
            <p className="m-0 text-[15px] text-muted">{t('give.none')}</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {recent.map((h) => (
                <li key={h.id} className="flex items-center gap-3 rounded-xl border-[1.5px] border-line bg-paper px-3.5 py-2.5">
                  <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate text-[15px] font-semibold">{h.toName}</span>
                    <span className={`text-[13px] ${h.acknowledged ? 'text-success-text' : 'text-muted'}`}>
                      {h.acknowledged ? t('give.accepted') : t('give.waiting')}
                    </span>
                  </span>
                  <Num className="shrink-0 text-[17px] font-bold">−{rs(h.amount)}</Num>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

/** Whom to give to: one dropdown with every spender here (the phone's own picker). */
function PersonPicker(props: { people: HandoverPerson[]; value: string; onChange: (walletId: string) => void }) {
  const { t } = useT();
  return (
    <span className="relative flex">
      <select
        aria-label={t('give.to')}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className={`h-14 w-full appearance-none rounded-xl border-[1.5px] bg-paper ps-3.5 pe-11 text-[16px] font-semibold focus:border-ink focus:outline-none ${
          props.value ? 'border-ink' : 'border-line-strong text-muted'
        }`}
      >
        <option value="" disabled>
          {t('give.choose')}
        </option>
        {props.people.map((p) => (
          <option key={p.walletId} value={p.walletId} className="text-ink">
            {p.name}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute inset-y-0 end-3.5 flex items-center text-muted">
        <Icon name="chevronDown" size={20} stroke={2.25} />
      </span>
    </span>
  );
}
